import { TranslationKey } from "../i18n/translation-key"
import { STORE_TYPE, StoreType } from "../store/store-type"

export enum MessageVariable {
  TOTAL_COST = "#jobb-totalkostnad",
  BIKE_TAG = "#jobb-cykel-tagg",

  CUSTOMER_FIRST_NAME = "#kund-förnamn",

  OPENING_HOURS = "#butik-öppetider",
  TODAYS_CLOSING_TIME = "#butik-dagens-stängningstid",
  STORE_GREETING = "#butik-hälsningsfras",
  STORE_GOOGLE_MAPS_LINK = "#butik-google-maps-länk",
  STORE_BOOKING_LINK = "#butik-bokningslänk",

  BOOKING_CUSTOMER_MESSAGE = "#bokning-kundmeddelande",
  BOOKING_HAND_IN_DATE = "#bokning-inlämningsdag",
  BOOKING_ACTIONS = "#bokning-åtgärder",

  JOB_ACTIONS = "#jobb-åtgärder",
  JOB_PHYSICAL_WORKSHOP_INFO = "#jobb-verkstadsåtgärder-info",

  PARKING_FEE_PER_DAY = "#parkeringsavgift-per-dag",
  PARKING_FEE_CURRENT = "#parkeringsavgift-nuvarande-avgift",
}

/**
 * The variables whose value is the same for every job in a store, so a text
 * going to several customers at once can show them resolved.
 */
export type StoreMessageVariable =
  | MessageVariable.OPENING_HOURS
  | MessageVariable.TODAYS_CLOSING_TIME
  | MessageVariable.STORE_GREETING
  | MessageVariable.STORE_GOOGLE_MAPS_LINK
  | MessageVariable.STORE_BOOKING_LINK
  | MessageVariable.PARKING_FEE_PER_DAY

export interface MessageVariableInfo {
  variable: MessageVariable
  labelKey: TranslationKey
  descriptionKey: TranslationKey
  // Only offered to these store types; offered to every store when omitted.
  storeTypes?: StoreType[]
}

export const MESSAGE_VARIABLES: MessageVariableInfo[] = [
  {
    variable: MessageVariable.TOTAL_COST,
    labelKey: "common.messageVariable.totalCost.label",
    descriptionKey: "common.messageVariable.totalCost.description",
  },
  {
    variable: MessageVariable.BIKE_TAG,
    labelKey: "common.messageVariable.bikeTag.label",
    descriptionKey: "common.messageVariable.bikeTag.description",
  },
  {
    variable: MessageVariable.CUSTOMER_FIRST_NAME,
    labelKey: "common.messageVariable.customerFirstName.label",
    descriptionKey: "common.messageVariable.customerFirstName.description",
  },
  {
    variable: MessageVariable.OPENING_HOURS,
    labelKey: "common.messageVariable.openingHours.label",
    descriptionKey: "common.messageVariable.openingHours.description",
  },
  {
    variable: MessageVariable.TODAYS_CLOSING_TIME,
    labelKey: "common.messageVariable.todaysClosingTime.label",
    descriptionKey: "common.messageVariable.todaysClosingTime.description",
  },
  {
    variable: MessageVariable.STORE_GREETING,
    labelKey: "common.messageVariable.storeGreeting.label",
    descriptionKey: "common.messageVariable.storeGreeting.description",
  },
  {
    variable: MessageVariable.STORE_GOOGLE_MAPS_LINK,
    labelKey: "common.messageVariable.storeGoogleMapsLink.label",
    descriptionKey: "common.messageVariable.storeGoogleMapsLink.description",
  },
  {
    variable: MessageVariable.STORE_BOOKING_LINK,
    labelKey: "common.messageVariable.storeBookingLink.label",
    descriptionKey: "common.messageVariable.storeBookingLink.description",
  },
  {
    variable: MessageVariable.BOOKING_CUSTOMER_MESSAGE,
    labelKey: "common.messageVariable.bookingCustomerMessage.label",
    descriptionKey: "common.messageVariable.bookingCustomerMessage.description",
  },
  {
    variable: MessageVariable.BOOKING_HAND_IN_DATE,
    labelKey: "common.messageVariable.bookingHandInDate.label",
    descriptionKey: "common.messageVariable.bookingHandInDate.description",
  },
  {
    variable: MessageVariable.BOOKING_ACTIONS,
    labelKey: "common.messageVariable.bookingActions.label",
    descriptionKey: "common.messageVariable.bookingActions.description",
  },
  {
    variable: MessageVariable.JOB_ACTIONS,
    labelKey: "common.messageVariable.jobActions.label",
    descriptionKey: "common.messageVariable.jobActions.description",
  },
  {
    variable: MessageVariable.JOB_PHYSICAL_WORKSHOP_INFO,
    labelKey: "common.messageVariable.jobPhysicalWorkshopInfo.label",
    descriptionKey:
      "common.messageVariable.jobPhysicalWorkshopInfo.description",
    storeTypes: [STORE_TYPE.MOBILE],
  },
  // Only physical stores have bikes waiting to be picked up
  {
    variable: MessageVariable.PARKING_FEE_PER_DAY,
    labelKey: "common.messageVariable.parkingFeePerDay.label",
    descriptionKey: "common.messageVariable.parkingFeePerDay.description",
    storeTypes: [STORE_TYPE.PHYSICAL],
  },
  {
    variable: MessageVariable.PARKING_FEE_CURRENT,
    labelKey: "common.messageVariable.parkingFeeCurrent.label",
    descriptionKey: "common.messageVariable.parkingFeeCurrent.description",
    storeTypes: [STORE_TYPE.PHYSICAL],
  },
]

export function getMessageVariablesForStoreType(
  storeType: StoreType,
): MessageVariableInfo[] {
  return MESSAGE_VARIABLES.filter(
    (v) => !v.storeTypes || v.storeTypes.includes(storeType),
  )
}

const VARIABLE_PATTERN = new RegExp(
  MESSAGE_VARIABLES.map((v) =>
    v.variable.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  ).join("|"),
  "g",
)

export function contentContainsVariables(content: string): boolean {
  // Reset regex state since it uses the global flag
  VARIABLE_PATTERN.lastIndex = 0
  return VARIABLE_PATTERN.test(content)
}

export function substituteMessageVariables(
  content: string,
  // A variable without a value is left as its tag.
  values: Partial<Record<MessageVariable, string>>,
): string {
  // Reset regex state since it uses the global flag
  VARIABLE_PATTERN.lastIndex = 0

  let result = content.replace(VARIABLE_PATTERN, (match) => {
    const variable = match as MessageVariable
    return values[variable] ?? match
  })

  // Clean up artifacts from empty substitutions:
  // - Multiple consecutive newlines → max two
  result = result.replace(/\n{3,}/g, "\n\n")
  // - Trailing whitespace on lines
  result = result.replace(/[ \t]+\n/g, "\n")
  // - Leading/trailing whitespace
  result = result.trim()

  return result
}

/**
 * The variables that can be filled in for a message to a store customer that
 * isn't about a job: the store's own details and the customer's name.
 */
export type StoreCustomerMessageVariable =
  | StoreMessageVariable
  | MessageVariable.CUSTOMER_FIRST_NAME

const STORE_CUSTOMER_MESSAGE_VARIABLES: MessageVariable[] = [
  MessageVariable.CUSTOMER_FIRST_NAME,
  MessageVariable.OPENING_HOURS,
  MessageVariable.TODAYS_CLOSING_TIME,
  MessageVariable.STORE_GREETING,
  MessageVariable.STORE_GOOGLE_MAPS_LINK,
  MessageVariable.STORE_BOOKING_LINK,
  MessageVariable.PARKING_FEE_PER_DAY,
]

export function getMessageVariablesForStoreCustomer(
  storeType: StoreType,
): MessageVariableInfo[] {
  return getMessageVariablesForStoreType(storeType).filter((v) =>
    STORE_CUSTOMER_MESSAGE_VARIABLES.includes(v.variable),
  )
}

/** False when the content uses a variable that needs a job to resolve. */
export function contentFitsStoreCustomerMessage(content: string): boolean {
  VARIABLE_PATTERN.lastIndex = 0
  const used = content.match(VARIABLE_PATTERN) ?? []
  return used.every((match) =>
    STORE_CUSTOMER_MESSAGE_VARIABLES.some((variable) => variable === match),
  )
}
